import { Controller } from '@hotwired/stimulus';

// Room around the error, so its label and help text are not glued to the edges of the screen
const ROOM_ABOVE = 24;
const ROOM_BELOW = 48;

/**
 * Brings the first error of a form that failed server-side validation into view and focuses its field,
 * so the page does not reload at the top with the error below the fold.
 */
export default class extends Controller {
    static targets = ['summary'];

    connect() {
        const fields = this.element.querySelectorAll('.is-invalid');
        if (0 === fields.length) {
            this.revealMessage();
            return;
        }

        const field = fields[0];
        const label = field.labels?.[0] ?? field;
        // The summary of the form theme only leads when it fits on the screen together with the field
        const isSummaryLeading = this.hasSummaryTarget && this.fitsOnScreen(this.summaryTarget, field);
        const top = isSummaryLeading ? this.summaryTarget : label;

        this.bringIntoView(top, field);
        field.focus({ preventScroll: true });
    }

    /** An error without a field, such as an expired form or a failed upload */
    revealMessage() {
        const message = this.element.querySelector('.invalid-feedback, [data-alert="danger"]');
        if (!message) {
            return;
        }

        message.tabIndex = -1;
        message.classList.add('outline-none');
        this.bringIntoView(message, message);
        message.focus({ preventScroll: true });
    }

    fitsOnScreen(top, bottom) {
        return bottom.getBoundingClientRect().bottom - top.getBoundingClientRect().top + ROOM_ABOVE + ROOM_BELOW
            <= window.innerHeight - this.coveredTop();
    }

    bringIntoView(top, bottom) {
        const isInView = this.coveredTop() + ROOM_ABOVE <= top.getBoundingClientRect().top
            && bottom.getBoundingClientRect().bottom + ROOM_BELOW <= window.innerHeight;
        if (isInView) {
            return;
        }

        const isMotionReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        // Adds to the page's scroll padding, which already clears the sticky top bar on phones
        top.style.scrollMarginTop = `${ROOM_ABOVE}px`;
        top.scrollIntoView({ block: 'start', behavior: isMotionReduced ? 'auto' : 'smooth' });
    }

    /** The part of the screen a sticky top bar covers, as the page's scroll padding declares it */
    coveredTop() {
        return parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
    }
}
